import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { UseCases } from "@planzone/core";
import {
  useAssignTag,
  useMilestoneEdition,
  useRetrieveConstraints,
} from "@planzone/view-models";
import {
  Assignable,
  ProjectElementType,
  Removable,
  list,
  OptimizedGantt,
  Identifiable,
  Milestone as M,
} from "@planzone/dto";
import { MilestoneIcon } from "@common/milestoneIcon";
import { useModal } from "@root/client/modal";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { EditIcon } from "@common/editIcon";
import { TrashIcon } from "@common/trashIcon";
import { TransferIcon } from "@common/transferIcon";
import { ActivityWatcher } from "@common/activityWatcher";
import { ConstraintsPopup } from "@common/constraintsPopup";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { CommentsCount } from "../commentsCount";
import { AttachmentsCount } from "../attachmentsCount";
import { SecondaryCriteria } from "../secondaryCriteria";
import { Tags } from "../tags";
import { useProjectCtx } from "../projectCtx";
import { isBlocked } from "../utils";
import { DateForm } from "./dateForm";
import { NameEditionForm } from "./nameEditionForm";
import { MilestoneCompletion } from "./milestoneCompletion";
import { Context } from "../context";
import { Props } from "./types";
import styles from "./styles.scss";

function projectElementToMilestone(
  element: Extract<
    OptimizedGantt[number],
    { type: ProjectElementType.MILESTONE }
  >,
): Identifiable<M> {
  return {
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    endDate: element.endDate,
    completedAt: element.completionDate,
    createdDate: element.createdDate || "",
    list: { ...list, uuid: element.parentUuid },
    state: element.state,
    tags: element.tags,
    attachments: [],
    commentsNbr: 0,
    attachmentsNbr: 0,
    keyRank: element.referenceKeyRank,
    attributes: element.attributes,
    minStartDate: element.minStartDate ?? "",
    maxEndDate: element.maxEndDate ?? "",
  };
}

function Milestone({ milestone, meta, minDate, maxDate }: Props) {
  const { t } = useTranslation();
  const realMilestone = projectElementToMilestone(milestone);
  const { constraints } = useRetrieveConstraints();
  const { hoveredElement } = React.useContext(Context);
  const { project, template } = useProjectCtx();
  const projectTimezone = project.timeZone || DateTime.local().zoneName;
  const { assignTag } = useAssignTag();
  const { isRequestPending, editMilestone } =
    useMilestoneEdition(realMilestone);
  const isCompleted = milestone.state === "DONE";
  const { pushView } = useModal();
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const elementConstraints = constraints.filter(
    ({ targetEntityUuid, originEntityUuid }) =>
      targetEntityUuid === milestone.uuid ||
      originEntityUuid === milestone.uuid,
  );
  const isConstrained = constraints.length > 0;
  const blocked = isBlocked(milestone, elementConstraints);

  ActivityWatcher.useActivityWatcher(ref, {
    focus: () => setShowContextualMenu(!showContextualMenu),
    blur: () => setShowContextualMenu(false),
  });

  return (
    <div
      className={classNames(styles.milestone, {
        [styles.completed]: isCompleted,
        [styles.constrained]: blocked,
        [styles.hovered]:
          !!hoveredElement && hoveredElement.uuid === milestone.uuid,
      })}
    >
      <SecondaryCriteria project={project}>
        {() => ({
          main: (
            <div
              className={styles.milestoneDetails}
              onClick={() =>
                pushView({
                  key: UseCases.retrieveMilestone,
                  data: { milestone: realMilestone },
                })
              }
            >
              <div className={styles.name}>
                <MilestoneIcon className={styles.icon} milestone={milestone} />
                <NameEditionForm
                  milestone={realMilestone}
                  hovered={
                    !!hoveredElement && hoveredElement.uuid === milestone.uuid
                  }
                />
              </div>
              {isConstrained && (
                <div
                  className={styles.constraintsPopup}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ConstraintsPopup
                    elementId={milestone.uuid}
                    elementType="milestone"
                    constraintsNbr={elementConstraints.length}
                    project={project}
                  />
                </div>
              )}
            </div>
          ),
          criteria: {
            statuses: (
              <div className={styles.milestoneCompletion}>
                <MilestoneCompletion milestone={realMilestone} />
              </div>
            ),
            tags: (
              <p className={styles.tags}>
                <Tags
                  tags={milestone.tags}
                  project={project}
                  removable={Removable.MILESTONE}
                  uuid={milestone.uuid}
                  add={(tag) =>
                    assignTag({
                      tag,
                      assignable: Assignable.MILESTONE,
                      assignableId: milestone.uuid,
                      project,
                    })
                  }
                />
              </p>
            ),
            endDate: (
              <div className={styles.endDate}>
                <DateForm
                  milestone={realMilestone}
                  value={
                    milestone.endDate
                      ? (DateTime.fromISO(milestone.endDate, { zone: "utc" })
                          .toUTC()
                          .toISO() ?? "")
                      : ""
                  }
                  projectTimezone={projectTimezone}
                  disabled={isRequestPending}
                  minDate={minDate}
                  maxDate={maxDate}
                  submit={(date) => {
                    const nextDt = DateTime.fromISO(date, { zone: "utc" });
                    const nextIso = nextDt.toUTC().toISO() ?? "";
                    const currentDt = milestone.endDate
                      ? DateTime.fromISO(milestone.endDate, { zone: "utc" })
                      : null;
                    editMilestone({
                      milestone: realMilestone,
                      nextMilestone: {
                        ...realMilestone,
                        endDate:
                          !!currentDt && nextDt.equals(currentDt)
                            ? ""
                            : nextIso,
                      },
                    });
                  }}
                />
              </div>
            ),
            completionDate: (
              <div className={styles.endDate}>
                {!!milestone.completionDate &&
                  (() => {
                    const dt = DateTime.fromISO(milestone.completionDate, {
                      zone: "utc",
                    }).setZone(projectTimezone);
                    return (
                      <p className={styles.completionDate}>
                        {dt.isValid
                          ? dt.toLocaleString(DateTime.DATE_SHORT)
                          : ""}
                      </p>
                    );
                  })()}
              </div>
            ),
            ...template.reduce(
              (acc, criteria) => ({
                ...acc,
                [criteria.uuid]: (
                  <SecondaryCriteriaForm
                    criteria={criteria}
                    value={
                      milestone.attributes
                        ? milestone.attributes[criteria.uuid] || ""
                        : ""
                    }
                    projectTimezone={projectTimezone}
                    disabled={isRequestPending}
                    submit={(value) =>
                      editMilestone({
                        milestone: realMilestone,
                        nextMilestone: {
                          ...realMilestone,
                          attributes: {
                            ...milestone.attributes,
                            [criteria.uuid]: value,
                          },
                        },
                      })
                    }
                  />
                ),
              }),
              {},
            ),
          },
          comments: (
            <CommentsCount
              subject={milestone.uuid}
              type="milestone"
              commentsNbr={meta.commentCount}
            />
          ),
          files: (
            <AttachmentsCount
              uuid={milestone.uuid}
              fileNbr={meta.attachmentCount}
            />
          ),
          contextualMenu: (
            <div
              className={classNames(styles.contextualMenu, {
                [styles.displayContextualMenu]: showContextualMenu,
              })}
            >
              <div ref={ref}>
                <SecureContextualMenu
                  context={{ milestone: realMilestone }}
                  replacement={<div className={styles.placeholder} />}
                  items={[
                    {
                      name: t("listsList.contextualMenu.edit"),
                      useCase: UseCases.editMilestone,
                      renderIcon: (className) => (
                        <EditIcon className={className} />
                      ),
                      act: () =>
                        pushView({
                          key: UseCases.retrieveMilestone,
                          data: { milestone: realMilestone },
                        }),
                    },
                    {
                      name: t("listsList.contextualMenu.move"),
                      useCase: UseCases.moveListElement,
                      renderIcon: (className) => (
                        <TransferIcon className={className} />
                      ),
                      act: () =>
                        pushView({
                          key: UseCases.moveListElement,
                          data: {
                            projectElementId: milestone.uuid,
                            projectElementType: ProjectElementType.MILESTONE,
                            currentParentList: {
                              ...list,
                              uuid: milestone.parentUuid,
                            },
                            project,
                          },
                        }),
                    },
                    {
                      name: t("listsList.contextualMenu.remove"),
                      useCase: UseCases.removeMilestone,
                      renderIcon: (className) => (
                        <TrashIcon
                          className={classNames(className, styles.removeIcon)}
                        />
                      ),
                      color: "#db5f63",
                      act: () =>
                        pushView({
                          key: UseCases.removeMilestone,
                          data: realMilestone,
                        }),
                    },
                  ]}
                />
              </div>
            </div>
          ),
        })}
      </SecondaryCriteria>
    </div>
  );
}

export { Milestone };
